"use client";

import type { ImgHTMLAttributes, Ref } from "react";

import { cn, mergeRefs } from "../../utils";

export type ImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, "src"> & {
  src?: string;
  ref?: Ref<HTMLImageElement>;
};

type LoadState = "true" | "error";

const setLoaded = (image: HTMLImageElement, loaded: LoadState) => image.setAttribute("data-loaded", loaded);

// A server-rendered image can settle before hydration, while React isn't listening for `load` or `error` yet, so the
// element is checked once it mounts too.
const syncSettled = (image: HTMLImageElement | null) => {
  if (image?.complete) {
    setLoaded(image, image.naturalWidth > 0 ? "true" : "error");
  }
};

/**
 * An `<img>` that pulses as its own skeleton while it loads. It carries its load state as `data-loaded`: `"false"`,
 * then `"true"` or `"error"`, so it and what surrounds it style off `data-[loaded=…]`, `peer-data-[loaded=…]` or
 * `has-[[data-loaded=…]]` (to swap in a fallback on error, say) rather than React state.
 */
const Image = ({ ref, src, alt, className, onLoad, onError, ...props }: ImageProps) => (
  <img
    // React doesn't rewrite an attribute whose prop hasn't changed, so only a fresh element starts a new `src` back at
    // `"false"`.
    key={src}
    {...props}
    src={src}
    alt={alt}
    data-loaded="false"
    onLoad={event => {
      setLoaded(event.currentTarget, "true");
      onLoad?.(event);
    }}
    onError={event => {
      setLoaded(event.currentTarget, "error");
      onError?.(event);
    }}
    ref={ref ? mergeRefs(ref, syncSettled) : syncSettled}
    className={cn("data-[loaded=false]:animate-pulse data-[loaded=false]:bg-neutral-90", className)}
  />
);

Image.displayName = "Image";
export { Image };
